import React, { useMemo, useEffect } from 'react'
import { connect, useDispatch } from 'react-redux'
import { List } from 'immutable'
import styled from 'styled-components'
import { itemHelper, projectHelper } from '../../common/src/helpers'
import { addTag, removeTag } from '../../common/src/actions/tagsAPI'
import { SimpleModal } from './SimpleModal'
import { mapValuesToJsArray } from '../../common/src/helpers/immutableHelpers'
import { createItemFromTag, SuggestionsTagPicker } from '../pickers/SuggestionsTagPicker'
import { AMPLITUDE_ACTION_TYPES, dispatchEvent } from '../../common/src/eventTracking/amplitudeEvents'
import { itemTagsSelector, projectTagsSelector } from '../../actions/viewInitializationActions'
import { itemTagsMapSelector, projectTagsMapSelector } from '../../common/src/selectors/tagsSelectors'
import { useMergeState } from '../../common/src/hooks/enhancedHooks'
import { useTranslation } from 'react-i18next'

const StyledSuggestionsTagPicker = styled(SuggestionsTagPicker)`
  max-width: 500px;
`

const TYPE = {
  PROJECT: 'PROJECT',
  ITEM: 'ITEM',
}

const initMap = {
  [TYPE.ITEM]: itemTagsSelector,
  [TYPE.PROJECT]: projectTagsSelector,
}

const TagsSelectorModal_ = ({ object, tags, readOnly = false, type, onDismiss, ...rest }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { isOpen } = rest
  const [state, setState] = useMergeState({
    loading: false,
    errorMessage: null,
  })

  useEffect(() => {
    if (isOpen) {
      const init = initMap[type]
      dispatch(init())
    }
  }, [dispatch, isOpen, type])

  const onInsertTagFilter = tagName => {
    setState({
      loading: true,
      errorMessage: null,
    })
    return dispatch(addTag({ tagName, object })).then(onSuccessAddingTag)
  }

  const onRemoveTagFilter = tagName => {
    setState({
      loading: true,
      errorMessage: null,
    })
    return dispatch(removeTag({ tagName, object })).then(onSuccessRemovingTag)
  }
  const onTagsFilterError = error => {
    setState({ errorMessage: error })
  }

  const onSuccessAddingTag = ev => {
    const { error } = ev
    const newMessageError = error ? t('item.tag.error_adding') : null
    dispatch(dispatchEvent(AMPLITUDE_ACTION_TYPES.ADD_TAG))
    setState({
      loading: false,
      errorMessage: newMessageError,
    })
  }

  const onSuccessRemovingTag = ev => {
    const { error } = ev
    const newMessageError = error ? t('item.tag.error_removing') : null
    dispatch(dispatchEvent(AMPLITUDE_ACTION_TYPES.REMOVE_TAG))
    setState({
      loading: false,
      errorMessage: newMessageError,
    })
  }

  const titleMap = useMemo(() => {
    return {
      [TYPE.ITEM]: t('item.tag.item'),
      [TYPE.PROJECT]: t('item.tag.project'),
    }
  }, [t])

  const objectTags =
    (projectHelper.isProject(object) ? projectHelper.getTags(object) : itemHelper.getTags(object)) || List()
  const selectedItems = mapValuesToJsArray(objectTags, createItemFromTag)
  const suggestedItems = mapValuesToJsArray(tags, createItemFromTag)
  const { loading, errorMessage } = state
  const title = titleMap[type]
  const disabled = loading || readOnly
  return (
    <SimpleModal
      title={title}
      subtitle={t('item.tag.add_tags')}
      loading={loading}
      errorMessage={errorMessage}
      onDismiss={onDismiss}
      {...rest}
    >
      <StyledSuggestionsTagPicker
        items={selectedItems}
        suggestedItems={suggestedItems}
        autoFocus
        onInsert={onInsertTagFilter}
        onRemove={onRemoveTagFilter}
        onError={onTagsFilterError}
        onFinish={onDismiss}
        disabled={disabled}
      />
    </SimpleModal>
  )
}

export const ItemTagsSelectorModal = connect(state => {
  return {
    tags: itemTagsMapSelector(state).toList(),
    type: TYPE.ITEM,
  }
})(TagsSelectorModal_)

export const ProjectTagsSelectorModal = connect(state => {
  return {
    tags: projectTagsMapSelector(state).toList(),
    type: TYPE.PROJECT,
  }
})(TagsSelectorModal_)
